import { WhyChooseItem } from '../data/whyChooseData';
import { ReviewItem } from '../data/reviewsData';
// import { contactFormFields as defaultForms } from '../data/formsData';
import type { ContactFormFields } from '../data/formsData';
import { /*WEEK1, WEEK2,*/ MealsWeek } from '../data/mealsData';
import { FeatureItem } from '../data/featuresData';

// ---------- WHY CHOOSE ----------
export type WhyChoosePatch = Record<string, Partial<Pick<WhyChooseItem, 'number' | 'title' | 'description' | 'imageAlt'>>>;

export function applyWhyChoosePatch(defaults: readonly WhyChooseItem[], patch: WhyChoosePatch | null) {
  if (!patch) return defaults;
  const map = new Map(defaults.map(i => [i.id, i]));
  const out: WhyChooseItem[] = defaults.map(item => {
    const p = patch[item.id];
    return p ? { ...item, ...p } : item;
  });
  // Include any items that exist only in patch (will render if you also add them to your TS later)
  Object.keys(patch).forEach(id => {
    if (!map.has(id)) {
      const p = patch[id]!;
      out.push({
        id,
        number: p.number ?? '',
        title: p.title ?? '',
        description: p.description ?? '',
        image: '', // will be empty until you add to TS; page can warn
        imageAlt: p.imageAlt ?? '',
      } as WhyChooseItem);
    }
  });
  return out;
}

// ---------- REVIEWS ----------
export type ReviewsPatch = ReviewItem[]; // full list—CRUD

export function applyReviewsPatch(defaults: readonly ReviewItem[], patch: ReviewsPatch | null) {
  return patch ?? defaults;
}

// ---------- PARENTS ----------
export type ParentsPatch = {
  hero?: { title?: string; subtitle?: string };
  aims?: string[];
  vision?: string;
  why?: string[];
  services?: { bullets?: string[]; note?: string };
  funding?: Array<{ id: string; title: string; body: string; links?: Array<{label: string; href: string}> }>;
};

export function applyParentsPatch(defaults: any, patch: ParentsPatch | null) {
  if (!patch) return defaults;
  return {
    ...defaults,
    hero: {
      ...defaults.hero,
      title: patch.hero?.title ?? defaults.hero.title,
      subtitle: patch.hero?.subtitle ?? defaults.hero.subtitle,
    },
    aims: patch.aims ?? defaults.aims,
    vision: patch.vision ?? defaults.vision,
    // keep visionImageSrc from defaults
    why: patch.why ?? defaults.why,
    services: {
      ...defaults.services,
      bullets: patch.services?.bullets ?? defaults.services.bullets,
      note: patch.services?.note ?? defaults.services.note,
      // keep imageSrc from defaults
    },
    funding: patch.funding ?? defaults.funding,
  };
}

// ---------- NEWS ----------
export type NewsPatchItem = {
  id: string;
  title?: string;
  date?: string;
  excerpt?: string;
  link?: string;
  image?: string;
  imageAlt?: string;
};

export type NewsPatch = NewsPatchItem[];

// copy only defined values from src to dst
const mergeDefined = <T extends object>(dst: T, src: Partial<T>): T => {
  const out = { ...dst } as any;
  for (const k in src) {
    if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
    const v = (src as any)[k];
    if (v !== undefined) out[k] = v; // keep only defined (don’t overwrite with undefined)
  }
  return out as T;
};

export function applyNewsPatch(defaults: readonly any[], patch: NewsPatch | null) {
  if (!patch || patch.length === 0) {
    // no patch → just return defaults as-is
    return [...defaults];
  }

  // Index defaults by id for fast lookup
  const defById = new Map<string, any>(defaults.map(d => [d.id, d]));
  const seen = new Set<string>();
  const result: any[] = [];

  // 1) Emit items in the patch order, merging with defaults or creating new
  for (const p of patch) {
    const def = defById.get(p.id);
    if (def) {
      // merge defined patch fields over defaults
      result.push(mergeDefined(def, p));
    } else {
      // NEW item (not in defaults). Use only what the patch provides.
      // IMPORTANT: do NOT force image = '' here.
      result.push({ ...p });
    }
    seen.add(p.id);
  }

  // 2) Append untouched defaults (not referenced by patch)
  for (const d of defaults) {
    if (!seen.has(d.id)) result.push(d);
  }

  return result;
}

// ---------- MEALS ----------
export type MealsPatch = MealsWeek; // same shape as week data, text fields only

export function applyMealsPatch(defaultWeek: MealsWeek, patch: MealsPatch | null) {
  return patch ? { ...defaultWeek, ...patch } : defaultWeek;
}

// ---------- FORMS ----------
export type FormsPatch = {
  personal?: Array<{ name: string; placeholder?: string; required?: boolean }>;
  child?: Array<{ name: string; placeholder?: string; required?: boolean }>;
};

export function applyFormsPatch(defaults: ContactFormFields, patch: FormsPatch | null): ContactFormFields {
  const apply = (defaultsArr: readonly any[], patchArr?: Array<any>) => {
    if (!patchArr) return defaultsArr;
    // const byName = new Map(defaultsArr.map((f:any)=>[f.name,f]));
    const out = defaultsArr.map((f:any) => {
      const p = patchArr.find((x:any)=>x.name===f.name);
      return p ? { ...f, placeholder: p.placeholder ?? f.placeholder, required: typeof p.required === 'boolean' ? p.required : f.required } : f;
    });
    // Ignore new names not in defaults to keep types stable
    return out;
  };
  return {
    personal: apply(defaults.personal, patch?.personal),
    child: apply(defaults.child, patch?.child),
  } as ContactFormFields;
}

// ---------- FEES ----------
export type FeesPatch = {
  FEES?: Array<{ ageLabel: string; rates: { early: number; regular: number; late: number } }>;
  DAYS?: Array<{ key: string; label: string }>;
  ACCENT?: { red?: string; yellow?: string };
};

export function applyFeesPatch(defaults: any, patch: FeesPatch | null) {
  if (!patch) return defaults;
  return {
    ...defaults,
    FEES: patch.FEES ?? defaults.FEES,
    DAYS: patch.DAYS ?? defaults.DAYS,
    ACCENT: { ...defaults.ACCENT, ...(patch.ACCENT || {}) },
  };
}

// ---------- FEATURES ----------
export type FeaturesPatchItem = Omit<FeatureItem, 'image'> & { image?: never };
export type FeaturesPatch = FeaturesPatchItem[];

export function applyFeaturesPatch(
  defaults: readonly FeatureItem[],
  patch: FeaturesPatch
): FeatureItem[] {
  // Step 1: If there's no patch, just return a mutable copy of defaults
  if (!patch) {
    return [...defaults];
  }

  // Step 2: Apply patch logic — here I'm assuming patch items match by index
  const patched = defaults.map((item, index) => {
    const patchItem = patch[index];
    return patchItem ? { ...item, ...patchItem } : item;
  });

  // Step 3: Ensure result is mutable
  return [...patched];
}

// ---------- ABOUT ----------
export type AboutPatch = {
  ourStory?: { heading?: string; paragraphs?: string[] };
  team?: Array<{ id: string; name: string; position: string; bio: string }>;
  accreditations?: Array<{ id: string; name: string; url?: string }>;
};

export function applyAboutPatch(defaults: any, patch: AboutPatch | null) {
  if (!patch) return defaults;
  return {
    ...defaults,
    ourStory: {
      ...defaults.ourStory,
      heading: patch.ourStory?.heading ?? defaults.ourStory.heading,
      paragraphs: patch.ourStory?.paragraphs ?? defaults.ourStory.paragraphs,
    },
    team: patch.team ? patch.team.map(p => {
      const def = (defaults.team || []).find((t:any)=>t.id===p.id);
      return def ? { ...def, ...p } : { ...p, photo: defaults.ourStory?.image }; // keep photos only if exist in TS
    }) : defaults.team,
    accreditations: patch.accreditations ? patch.accreditations.map(p => {
      const def = (defaults.accreditations || []).find((a:any)=>a.id===p.id);
      return def ? { ...def, ...p } : { ...p, logo: { src: '', alt: '' } };
    }) : defaults.accreditations,
  };
}
